import { useEffect, useState } from 'preact/hooks';
import { getCoachHistory, type CoachHistoryEntryApi } from '../lib/api';

// CoachStudents — THE DOOR to `#/pro/students/:label` (rung 3).
//
// Spec: `spec-the-outside-professional-surface-coaches-and-agents-2026-08-26` §8.2
//
// ⚑ THIS EXISTS BECAUSE THE STUDENT PAGE OTHERWISE HAD NO WAY IN. The queue's
// rows cannot be that door: a queue row is built from a GRANT plus the portal's
// chain walk, which yields role and project but never the performer — and the
// student page is keyed on the performer. So a coach could have reached their
// own students only by typing a URL. "Verify the screen is reachable" has bitten
// this codebase twice; `ScreenReachability.test.ts` exists because three things
// were built onto a screen nothing navigated to.
//
// ⚑ IT IS BUILT FROM `coach_review_log`, NOT FROM GRANTS, and that is what
// makes it honest over time: a students list assembled from live grants would
// quietly lose a student the moment their last link lapsed — the exact erosion
// ruling 4 forbids.

interface StudentRow {
  label: string;
  count: number;
  latest: string;
}

/** Group the flat history into students, most recently seen first. */
export function studentsFrom(entries: CoachHistoryEntryApi[]): StudentRow[] {
  const byLabel = new Map<string, StudentRow>();
  for (const e of entries) {
    // A note whose household never named the performer has nowhere to sit.
    // Skipped rather than bucketed under "Unknown": a fake student is worse
    // than an absent one, and the note is still in the full history.
    const label = e.student?.trim();
    if (!label) continue;
    const existing = byLabel.get(label);
    if (existing) {
      existing.count += 1;
      if (e.reviewedAt > existing.latest) existing.latest = e.reviewedAt;
    } else {
      byLabel.set(label, { label, count: 1, latest: e.reviewedAt });
    }
  }
  return [...byLabel.values()].sort((a, b) => (a.latest < b.latest ? 1 : -1));
}

export function CoachStudents() {
  const [students, setStudents] = useState<StudentRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCoachHistory()
      .then((r) => {
        if (cancelled) return;
        setStudents(studentsFrom(r.entries));
      })
      .catch(() => {
        // Quiet here, deliberately, and this is the ONE place on the surface
        // where quiet is right: the queue above already says loudly when the
        // surface is broken, and repeating it twice on one screen turns a
        // clear signal into noise. An empty students list beside a working
        // queue is not misleading — a coach who has written nothing has no
        // students yet.
        if (!cancelled) setStudents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (students === null || students.length === 0) return null;

  return (
    <div class="coach-students">
      <h2 class="coach-students__title">Your students</h2>
      <ul class="coach-students__list">
        {students.map((s) => (
          <li key={s.label}>
            <a class="coach-students__link" href={`#/pro/students/${encodeURIComponent(s.label)}`}>
              <span class="coach-students__name">{s.label}</span>
              <span class="coach-students__count">
                {s.count} {s.count === 1 ? 'note' : 'notes'}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
